/**
 * Space announcement payloads.
 *
 * An announcement body is a {@link MessagePayload} plus an optional title.
 * The whole thing is the plaintext of an E2EE announcement (so the server
 * never sees the title) or the `content` of a plaintext one.
 *
 * In the composer, inline media appears as `[image N]` / `[gif N]` tokens
 * (1-based indices into the attachment and GIF trays). On send the tokens are
 * stripped from the text and recorded as {@link InlineMediaEntity} offsets;
 * editing reverses that.
 *
 * @module services/announcementPayload
 */

import {
  SPACE_ANNOUNCEMENT_TITLE_MAX_LENGTH,
  SPACE_ANNOUNCEMENT_TOAST_BODY_CHARS,
  SPACE_ANNOUNCEMENT_TOAST_TITLE_CHARS,
} from '@adieuu/shared';
import {
  parsePayload,
  type InlineMediaEntity,
  type MessagePayload,
  type ParsedMessagePayload,
} from './messagePayload';

export interface AnnouncementPayload extends MessagePayload {
  title?: string;
}

export interface ParsedAnnouncementPayload extends ParsedMessagePayload {
  inlineMedia: InlineMediaEntity[];
  /** Trimmed and capped; absent when empty. */
  title?: string;
}

/** Trim, cap at the title limit (by code point), and drop when empty. */
export function normalizeAnnouncementTitle(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const chars = Array.from(trimmed);
  return chars.length > SPACE_ANNOUNCEMENT_TITLE_MAX_LENGTH
    ? chars.slice(0, SPACE_ANNOUNCEMENT_TITLE_MAX_LENGTH).join('').trimEnd()
    : trimmed;
}

/**
 * Always JSON (unlike {@link serializePayload}) so a title-only or text-only
 * announcement still round-trips as a structured payload.
 */
export function serializeAnnouncementPayload(payload: Omit<AnnouncementPayload, 'version'>): string {
  const title = normalizeAnnouncementTitle(payload.title);
  const out: AnnouncementPayload = { version: 1 };
  if (title) out.title = title;
  if (payload.text) out.text = payload.text;
  if (payload.attachments?.length) out.attachments = payload.attachments;
  if (payload.gifAttachments?.length) out.gifAttachments = payload.gifAttachments;
  if (payload.inlineMedia?.length) out.inlineMedia = payload.inlineMedia;
  if (payload.customEmojis && Object.keys(payload.customEmojis).length > 0) {
    out.customEmojis = payload.customEmojis;
  }
  if (payload.senderDeviceId) out.senderDeviceId = payload.senderDeviceId;
  return JSON.stringify(out);
}

export function parseAnnouncementPayload(plaintext: string): ParsedAnnouncementPayload {
  const parsed = parsePayload(plaintext);
  let title: string | undefined;
  if (parsed.isStructured) {
    try {
      title = normalizeAnnouncementTitle((JSON.parse(plaintext) as { title?: unknown }).title);
    } catch {
      title = undefined;
    }
  }
  return {
    ...parsed,
    // Announcements never carry mentions or page tags; ignore any that slip in.
    mentions: [],
    pageTags: [],
    inlineMedia: parsed.inlineMedia ?? [],
    ...(title ? { title } : {}),
  };
}

const INLINE_TOKEN_RE = /\[(image|gif) (\d+)\]/g;

export function inlineMediaToken(kind: InlineMediaEntity['kind'], index: number): string {
  return `[${kind === 'media' ? 'image' : 'gif'} ${index + 1}]`;
}

/**
 * Strip `[image N]` / `[gif N]` tokens from composer text and record where
 * each stood. Tokens naming an item that does not exist, or one already
 * placed, are left as literal text.
 */
export function extractInlineMediaTokens(
  composerText: string,
  counts: { media: number; gif: number },
): { text: string; inlineMedia: InlineMediaEntity[] } {
  const inlineMedia: InlineMediaEntity[] = [];
  const placed = new Set<string>();
  let text = '';
  let last = 0;
  for (const match of composerText.matchAll(INLINE_TOKEN_RE)) {
    const kind: InlineMediaEntity['kind'] = match[1] === 'image' ? 'media' : 'gif';
    const index = Number(match[2]) - 1;
    const key = `${kind}:${index}`;
    if (index < 0 || index >= counts[kind] || placed.has(key)) continue;
    text += composerText.slice(last, match.index);
    inlineMedia.push({ offset: text.length, kind, index });
    placed.add(key);
    last = match.index + match[0].length;
  }
  text += composerText.slice(last);
  return { text, inlineMedia };
}

/** Trim surrounding whitespace, shifting and clamping offsets to match. */
export function trimInlineMediaText(
  text: string,
  inlineMedia: readonly InlineMediaEntity[],
): { text: string; inlineMedia: InlineMediaEntity[] } {
  const lead = text.length - text.trimStart().length;
  const trimmed = text.trim();
  return {
    text: trimmed,
    inlineMedia: inlineMedia.map((e) => ({
      ...e,
      offset: Math.min(Math.max(e.offset - lead, 0), trimmed.length),
    })),
  };
}

/** Rebuild composer text with tokens, for editing an existing announcement. */
export function insertInlineMediaTokens(
  text: string,
  inlineMedia: readonly InlineMediaEntity[],
): string {
  const sorted = [...inlineMedia].sort((a, b) => a.offset - b.offset);
  let out = '';
  let last = 0;
  for (const entity of sorted) {
    const offset = Math.min(Math.max(entity.offset, last), text.length);
    out += text.slice(last, offset) + inlineMediaToken(entity.kind, entity.index);
    last = offset;
  }
  return out + text.slice(last);
}

/**
 * After removing tray item `removedIndex`, drop its token and shift the
 * numbers of later items of the same kind down by one.
 */
export function removeInlineMediaToken(
  composerText: string,
  kind: InlineMediaEntity['kind'],
  removedIndex: number,
): string {
  const word = kind === 'media' ? 'image' : 'gif';
  return composerText.replace(INLINE_TOKEN_RE, (token, tokenWord: string, n: string) => {
    if (tokenWord !== word) return token;
    const index = Number(n) - 1;
    if (index === removedIndex) return '';
    return index > removedIndex ? `[${word} ${index}]` : token;
  });
}

function truncateChars(value: string, max: number): string {
  const chars = Array.from(value);
  return chars.length > max ? `${chars.slice(0, max - 1).join('').trimEnd()}…` : value;
}

/**
 * Toast text: the title when there is one, otherwise a whitespace-collapsed
 * excerpt of the body. Empty when the announcement is media-only.
 */
export function announcementToastSummary(
  payload: Pick<ParsedAnnouncementPayload, 'title' | 'text'>,
): { kind: 'title' | 'body'; text: string } {
  if (payload.title) {
    return { kind: 'title', text: truncateChars(payload.title, SPACE_ANNOUNCEMENT_TOAST_TITLE_CHARS) };
  }
  const body = payload.text.replace(/\s+/g, ' ').trim();
  return { kind: 'body', text: truncateChars(body, SPACE_ANNOUNCEMENT_TOAST_BODY_CHARS) };
}
